/**
 * Azure API Client
 *
 * Replaces Supabase client with direct Azure PostgreSQL API calls
 * Backend API: https://sms-pro-api.wonderfulflower-3a068fa9.uksouth.azurecontainerapps.io
 *
 * Multi-Organization Architecture:
 * - JWT tokens contain organizationId, role, and vessels
 * - All data is scoped by organization + vessel
 * - Database enforces isolation via foreign keys
 *
 * Authentication Flow:
 * 1. Login → POST /api/auth/login → JWT token
 * 2. Store token in localStorage
 * 3. Include token in Authorization header for all requests
 * 4. Token expires after 7 days
 */

// Azure API Configuration
// MIGRATION: Fallback URL removed — app must have REACT_APP_AZURE_API_URL set
const API_BASE_URL = process.env.REACT_APP_AZURE_API_URL;
if (!API_BASE_URL) {
  console.error('[API] REACT_APP_AZURE_API_URL is not set — API calls will fail');
}
const TOKEN_KEY = 'sms_pro_auth_token';
const USER_KEY = 'sms_pro_user';

// Retry configuration for rate limiting
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000; // 1 second base delay for exponential backoff

// FT-365 v2 Layer C: single-flight 401 → sr:auth:expired dispatch guard.
// Module-scope so concurrent 401s (parallel requests when a token expires
// mid-sync) don't fire multiple logout events. Reset naturally by the
// window.location.reload() that AuthGate's handler triggers.
//
// FT-365c: exported reset in case the reload path throws (wipe crashes AND
// reload throws inside AuthGate's logout catch). Without a manual reset the
// flag would stick forever, silently swallowing every subsequent 401. See
// AuthGate.jsx `logout()` catch branch and DESIGN-BRIEF §13.4.
let logoutInFlight = false;

// FT-365 v3.3: single-flight guard for getSession(). React's mount-effect
// firing twice on the AuthGate subtree (VesselProviderKeyedByAuth remounts
// during login when its key transitions empty → user.id) was firing
// checkAuthState → getSession twice ~100-140ms apart in prod. Doubling
// /auth/verify calls doubles the transient-401 blast radius. If a session
// check is already in flight, subsequent callers get the same promise.
let sessionCheckInFlight = null;

/**
 * FT-365c: reset the Layer C single-flight guard. Called from AuthGate.logout
 * when the wipe+reload flow throws — guarantees the next 401 can still fire
 * `sr:auth:expired`. Idempotent.
 */
export function resetLogoutInFlight() {
  logoutInFlight = false;
}

/**
 * Get stored JWT token
 * @returns {string|null} JWT token or null
 */
function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Get stored user data
 * @returns {Object|null} User object or null
 */
function getUser() {
  const userJson = localStorage.getItem(USER_KEY);
  if (!userJson) return null;

  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
}

/**
 * Store JWT token and user data
 * @param {string} token - JWT token
 * @param {Object} user - User object
 */
function storeAuth(token, user) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Clear authentication data
 */
function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Safely parse response body - handles non-JSON responses (e.g., 429 plain text)
 * @param {Response} response - Fetch response object
 * @returns {Promise<Object>} Parsed JSON or error object
 */
async function safeParseResponse(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      // JSON parse failed despite content-type header
      return { error: 'Invalid JSON response' };
    }
  }

  // Non-JSON response - read as text and wrap in error object
  try {
    const text = await response.text();
    return { error: text || `HTTP ${response.status}` };
  } catch {
    return { error: `HTTP ${response.status}` };
  }
}

/**
 * Delay helper for exponential backoff
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise<void>}
 */
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Make authenticated API request with retry for 429 rate limiting
 * @param {string} endpoint - API endpoint (e.g., '/sync/push')
 * @param {Object} options - Fetch options
 * @param {number} retryCount - Current retry attempt (internal use)
 * @returns {Promise<Response>}
 */
// eslint-disable-next-line complexity -- FT-365 v3.3: adds one branch for /auth/verify retry-once
async function apiRequest(endpoint, options = {}, retryCount = 0) {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const fetchOnce = () => fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });

  let response = await fetchOnce();

  // FT-365 v3.3: retry-once on 401 for /auth/verify specifically. Transient
  // 401 from replica cold-start / clock skew / mid-request eviction is
  // recoverable; a genuinely-invalid JWT will 401 on retry too. Only trigger
  // the full sr:auth:expired flow if the retry ALSO 401s. Scope is
  // intentionally narrow: session-verification is the only endpoint fired
  // reflexively on mount, so it's where a spurious 401 hurts most. All other
  // endpoints keep their existing immediate-logout behaviour below.
  if (response.status === 401 && endpoint === '/auth/verify') {
    // FT-365 v3.3 R3: use console.info (not devLog) so retry events are
    // visible in prod DevTools. Cheap observability — if a user reports
    // the bounce, they can screenshot the console for evidence.
    // eslint-disable-next-line no-console -- FT-365 v3.3 prod observability
    console.info('[FT-365 v3.3] /auth/verify returned 401 — retrying once in 300ms (transient replica-scaling recovery)');
    await delay(300);
    response = await fetchOnce();
    if (response.ok) {
      // eslint-disable-next-line no-console -- FT-365 v3.3 prod observability
      console.info('[FT-365 v3.3] /auth/verify retry succeeded — transient 401 recovered');
    } else {
      // eslint-disable-next-line no-console -- FT-365 v3.3 prod observability
      console.info('[FT-365 v3.3] /auth/verify retry also failed with status', response.status, '— proceeding to sr:auth:expired');
    }
  }

  // Handle 401 Unauthorized - token expired or invalid
  if (response.status === 401) {
    clearAuth();

    // FT-365 v2 Layer C: dispatch sr:auth:expired so AuthGate runs the SAME
    // full logout flow (wipe + reload) as a manual sign-out. Single-flight
    // guarded — reset on reload. NOTE: getSession()'s FT-235 offline guards
    // are UNCHANGED.
    // Reachability:
    //   - Non-/auth/verify 401s reach this dispatch immediately (unchanged
    //     from pre-v3.3 behaviour).
    //   - /auth/verify 401s reach this dispatch ONLY after the v3.3 retry
    //     above also returned non-2xx — a transient 401 is silently absorbed
    //     by the retry and never reaches here.
    if (!logoutInFlight && typeof window !== 'undefined') {
      logoutInFlight = true;
      try {
        window.dispatchEvent(new CustomEvent('sr:auth:expired'));
      } catch {
        // Non-fatal — throw below still surfaces the error to the caller.
      }
    }

    throw new Error('Authentication expired - please login again');
  }

  // Handle 429 Rate Limiting with exponential backoff
  if (response.status === 429 && retryCount < MAX_RETRIES) {
    // Check for Retry-After header (seconds to wait)
    const retryAfter = response.headers.get('Retry-After');
    let waitTime;

    if (retryAfter) {
      waitTime = parseInt(retryAfter, 10) * 1000; // Convert to ms
    } else {
      // Exponential backoff: 1s, 2s, 4s
      waitTime = BASE_DELAY_MS * Math.pow(2, retryCount);
    }

    console.log(`[AZURE API] Rate limited (429), retry ${retryCount + 1}/${MAX_RETRIES} in ${waitTime}ms`);
    await delay(waitTime);

    return apiRequest(endpoint, options, retryCount + 1);
  }

  return response;
}

/**
 * Azure API Client
 * Provides authentication and sync operations
 */
export const azureApiClient = {
  /**
   * Login with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{user: Object, token: string}>}
   */
  login: async (email, password) => {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await safeParseResponse(response);
      throw new Error(error.error || 'Login failed');
    }

    const data = await safeParseResponse(response);
    storeAuth(data.token, data.user);

    return { user: data.user, token: data.token };
  },

  /**
   * Register new user (if registration is enabled)
   * @param {Object} userData - User registration data
   * @returns {Promise<{user: Object, token: string}>}
   */
  register: async (userData) => {
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const error = await safeParseResponse(response);
      throw new Error(error.error || 'Registration failed');
    }

    const data = await safeParseResponse(response);
    storeAuth(data.token, data.user);

    return { user: data.user, token: data.token };
  },

  /**
   * Logout (clear local auth data)
   */
  logout: () => {
    clearAuth();
  },

  /**
   * Get current session (check if token is valid)
   * @returns {Promise<{user: Object|null}>}
   */
  getSession: async () => {
    // FT-365 v3.3: single-flight — if a session check is already in flight,
    // return its promise instead of firing a duplicate /auth/verify. React's
    // mount-effect on AuthGate can fire checkAuthState → getSession twice
    // ~100-140ms apart during login remount, and that doubled the transient-
    // 401 blast radius. FT-235 offline + network-error guards are preserved
    // inside the wrapped body — do NOT move them outside the wrapper.
    if (sessionCheckInFlight) {
      return sessionCheckInFlight;
    }

    sessionCheckInFlight = (async () => {
      try {
        const token = getToken();
        const user = getUser();

        if (!token || !user) {
          return { user: null };
        }

        // ⚠️ FT-235 OFFLINE-FIRST: Do NOT remove these two guards.
        // Previously, network errors called clearAuth() which permanently deleted
        // the token, kicking the user to the login screen on every offline refresh.
        // Rule: only clearAuth() on an explicit !response.ok (401/403 from server).
        // A thrown network error means we can't reach the server — NOT that the token is invalid.

        // Guard 1: If offline, skip verification entirely — trust the cached token
        if (!navigator.onLine) {
          return { user };
        }

        // Verify token is still valid
        try {
          const response = await apiRequest('/auth/verify', {
            method: 'POST'
          });

          if (!response.ok) {
            // Explicit server rejection (401/403) — token is genuinely invalid
            clearAuth();
            return { user: null };
          }

          return { user };
        } catch {
          // Guard 2: Network error (fetch threw) — token validity unknown, preserve session
          return { user };
        }
      } finally {
        sessionCheckInFlight = null;
      }
    })();

    return sessionCheckInFlight;
  },

  /**
   * Get current user
   * @returns {Object|null} User object or null
   */
  getCurrentUser: () => {
    return getUser();
  },

  /**
   * Push data to cloud
   * @param {string} collection - Collection name (e.g., 'crew')
   * @param {string} id - Document ID
   * @param {Object} data - Document data
   * @param {string} vesselId - Vessel ID
   * @returns {Promise<{success: boolean, timestamp?: number}>}
   */
  push: async (collection, id, data, vesselId) => {
    const response = await apiRequest('/sync/push', {
      method: 'POST',
      body: JSON.stringify({
        collection,
        id,
        data,
        vesselId
      })
    });

    if (!response.ok) {
      const error = await safeParseResponse(response);
      // FT-577: carry the HTTP status (e.g. 413 Payload too large) so the
      // outbox can record WHY a push failed instead of a bare message.
      const pushErr = new Error(error.error || 'Push failed');
      pushErr.status = response.status;
      throw pushErr;
    }

    return await safeParseResponse(response);
  },

  /**
   * Pull data from cloud
   * @param {string} collection - Collection name
   * @param {string} vesselId - Vessel ID
   * @param {number} since - Timestamp to get updates since (0 for all)
   * @returns {Promise<Array>} Array of documents
   */
  pull: async (collection, vesselId, since = 0) => {
    const url = `/sync/pull?collection=${collection}&vesselId=${vesselId}&since=${since}`;
    const response = await apiRequest(url);

    if (!response.ok) {
      const error = await safeParseResponse(response);
      throw new Error(error.error || 'Pull failed');
    }

    return await safeParseResponse(response);
  },

  /**
   * Get sync status
   * @param {string} vesselId - Vessel ID
   * @returns {Promise<{lastSyncAt: string, pendingChanges: number}>}
   */
  getSyncStatus: async (vesselId) => {
    const response = await apiRequest(`/sync/status?vesselId=${vesselId}`);

    if (!response.ok) {
      const error = await safeParseResponse(response);
      throw new Error(error.error || 'Status check failed');
    }

    return await safeParseResponse(response);
  },

  /**
   * Get list of vessels for current user
   * @returns {Promise<Array>} Array of vessels
   */
  getVessels: async () => {
    const response = await apiRequest('/vessels');

    if (!response.ok) {
      const error = await safeParseResponse(response);
      throw new Error(error.error || 'Failed to fetch vessels');
    }

    return await safeParseResponse(response);
  },

  /**
   * FT-436/FT-441 — Fleet-card attention data (VESSELS-ONLY).
   *
   * ONE batched, READ-ONLY GET returning the raw stored rows for every ASSIGNED
   * vessel so the fleet-landing cards can derive TRUE overdue/expiry counts even
   * for cold, never-opened-this-session vessels (which are resident in neither
   * localStorage nor IndexedDB). The vessel set is derived server-side from the
   * JWT (user_vessel_access — the SAME gate as GET /api/vessels); no ids are
   * sent, so scope can never be widened from the client.
   *
   * NOT YET DEPLOYED to the current prod API: this endpoint ships on the
   * facility/fleet API lineage. Until it is live the GET returns 404, which
   * throws here → the attention engine resolves EVERY vessel to `unknown`
   * ("Checking…") and NEVER paints a false "all up to date" (FT-441 safety
   * property). Nothing here writes storage or enqueues an outbox item.
   *
   * @returns {Promise<{ vessels: Array<{vesselId, collections}>, timestamp: number }>}
   */
  fetchFleetAttentionData: async () => {
    const response = await apiRequest('/fleet/attention-data');

    if (!response.ok) {
      const error = await safeParseResponse(response);
      throw new Error(error.error || 'Failed to fetch fleet attention data');
    }

    const result = await safeParseResponse(response);
    return {
      vessels: Array.isArray(result.vessels) ? result.vessels : [],
      timestamp: result.timestamp,
    };
  },

  /**
   * Register a new vessel in the user's organization
   * Requires admin role (org admin or super_admin)
   * @param {Object} vesselData - Vessel details (name required, others optional)
   * @returns {Promise<Object>} - Created vessel with UUID
   */
  registerVessel: async (vesselData) => {
    const response = await apiRequest('/vessels/register', {
      method: 'POST',
      body: JSON.stringify({
        name: vesselData.name,
        vesselType: vesselData.vesselType || vesselData.type,
        imo: vesselData.imo,
        flag: vesselData.flag,
        gt: vesselData.gt ? parseInt(vesselData.gt) : undefined
      })
    });

    const result = await safeParseResponse(response);

    if (!response.ok) {
      throw new Error(result.message || result.error || 'Failed to register vessel');
    }

    return result.vessel;
  },

  /**
   * FT-469: Register a vessel that was created OFFLINE, using the CLIENT's own
   * local UUID, under the caller's OWN org (org derived server-side from JWT).
   *
   * Why this exists: /sync/push no longer blind-auto-creates unknown vesselIds
   * (that allowed cross-org contamination). A genuinely-new offline vessel must
   * therefore be registered first so the subsequent push passes the server's
   * ownership guard. Idempotent server-side — safe to call on retry/replay.
   *
   * @param {string} vesselId - The client-generated local UUID for the vessel.
   * @param {Object} [meta] - Optional { name, vesselType, imo, flag, gt }.
   * @returns {Promise<Object>} - { id, organizationId, alreadyExisted }
   */
  registerOfflineVessel: async (vesselId, meta = {}) => {
    const response = await apiRequest('/vessels/register-offline', {
      method: 'POST',
      body: JSON.stringify({
        vesselId,
        name: meta.name,
        vesselType: meta.vesselType || meta.type,
        imo: meta.imo,
        flag: meta.flag,
        gt: meta.gt ? parseInt(meta.gt) : undefined
      })
    });

    const result = await safeParseResponse(response);

    if (!response.ok) {
      const err = new Error(result.message || result.error || 'Failed to register offline vessel');
      err.status = response.status;
      throw err;
    }

    return { ...(result.vessel || {}), alreadyExisted: !!result.alreadyExisted };
  },

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated: () => {
    return !!getToken();
  },

  /**
   * Get stored JWT token
   * @returns {string|null} JWT token or null
   */
  getToken: () => {
    return getToken();
  },

  /**
   * Get user's organization ID
   * @returns {string|null}
   */
  getOrganizationId: () => {
    const user = getUser();
    return user?.organizationId || null;
  },

  /**
   * Get user's role
   * @returns {string|null}
   */
  getRole: () => {
    const user = getUser();
    return user?.role || null;
  },

  /**
   * Get user's vessels
   * @returns {Array}
   */
  getUserVessels: () => {
    const user = getUser();
    return user?.vessels || [];
  },

  /**
   * Admin: Get all users (super_admin only)
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Results per page (default: 50)
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  adminGetUsers: async (page = 1, limit = 50) => {
    try {
      const response = await apiRequest(`/admin/users?page=${page}&limit=${limit}`);

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to fetch users' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.users || [] };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Create new user (super_admin only)
   * FT-103: Returns tempPassword from API response (API generates temp password, ignores client-sent password)
   * @param {Object} userData - User data
   * @returns {Promise<{success: boolean, data?: Object, tempPassword?: string, error?: string}>}
   */
  adminCreateUser: async (userData) => {
    try {
      const response = await apiRequest('/admin/users/create', {
        method: 'POST',
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to create user' };
      }

      const data = await safeParseResponse(response);
      // FT-103: Include tempPassword from API response
      return {
        success: true,
        data: data.user,
        tempPassword: data.tempPassword,
        tempPasswordExpiresAt: data.tempPasswordExpiresAt
      };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Reset user password (super_admin only)
   * @param {string} userId - User ID
   * @returns {Promise<{success: boolean, tempPassword?: string, error?: string}>}
   */
  adminResetPassword: async (userId) => {
    try {
      const response = await apiRequest('/admin/users/reset-password', {
        method: 'POST',
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to reset password' };
      }

      const data = await safeParseResponse(response);
      return { success: true, tempPassword: data.tempPassword };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * FT-395 (Cluster B): Update user details (super_admin only)
   * Server: PATCH /admin/users/:id — router-level requireSuperAdmin.
   * Body allowlist (updateUserSchema): name, email, role ('user'|'admin'|'super_admin'),
   * rbacRole ('super_admin'|'fleet'|'skipper'|'crew'), jobTitle (nullable), active.
   * The server enforces last-admin and cross-org guards; the client sends only the
   * fields being changed.
   * @param {string} userId - Target user ID
   * @param {Object} updates - Fields to update (allowlisted above)
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  adminUpdateUser: async (userId, updates) => {
    try {
      const response = await apiRequest(`/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.message || error.error || 'Failed to update user' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * FT-395 (Cluster B): Deactivate a user (super_admin only)
   * Server: POST /admin/users/:id/deactivate — no request body; target from :id.
   * Server enforces self-deactivation and last-admin guards.
   * @param {string} userId - Target user ID
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  adminDeactivateUser: async (userId) => {
    try {
      const response = await apiRequest(`/admin/users/${userId}/deactivate`, {
        method: 'POST'
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.message || error.error || 'Failed to deactivate user' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * FT-395 (Cluster B): Reactivate a user (super_admin only)
   * Server: POST /admin/users/:id/reactivate — no request body; target from :id.
   * @param {string} userId - Target user ID
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  adminReactivateUser: async (userId) => {
    try {
      const response = await apiRequest(`/admin/users/${userId}/reactivate`, {
        method: 'POST'
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.message || error.error || 'Failed to reactivate user' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * FT-108: Change user's own password (self-service)
   * @param {string} currentPassword - Current password for verification
   * @param {string} newPassword - New password to set
   * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
   */
  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await apiRequest('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword })
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.message || error.error || 'Failed to change password' };
      }

      const data = await safeParseResponse(response);
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * FT-132: Update user's own profile (self-service)
   * @param {Object} profileData - Profile data to update
   * @param {string} [profileData.name] - New display name
   * @param {string} [profileData.signatureImage] - Base64-encoded JPEG signature image
   * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
   */
  updateProfile: async (profileData) => {
    try {
      const response = await apiRequest('/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.message || error.error || 'Failed to update profile' };
      }

      const data = await safeParseResponse(response);

      // Update local storage with new user data
      if (data.user) {
        const currentUser = getUser();
        const updatedUser = { ...currentUser, ...data.user };
        localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
      }

      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Get all vessels (super_admin only)
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Results per page (default: 50)
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  adminGetVessels: async (page = 1, limit = 50) => {
    try {
      const response = await apiRequest(`/admin/vessels?page=${page}&limit=${limit}`);

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to fetch vessels' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.vessels || [] };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Create new vessel (super_admin only)
   * @param {Object} vesselData - Vessel data
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  adminCreateVessel: async (vesselData) => {
    try {
      const response = await apiRequest('/admin/vessels/create', {
        method: 'POST',
        body: JSON.stringify(vesselData)
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to create vessel' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.vessel };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Update vessel (super_admin only)
   * @param {string} vesselId - Vessel ID
   * @param {Object} updates - Vessel updates
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  adminUpdateVessel: async (vesselId, updates) => {
    try {
      const response = await apiRequest(`/admin/vessels/${vesselId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to update vessel' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.vessel };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Delete vessel (super_admin only)
   * @param {string} vesselId - Vessel ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  adminDeleteVessel: async (vesselId) => {
    try {
      const response = await apiRequest(`/admin/vessels/${vesselId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to delete vessel' };
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Grant vessel access to user (super_admin only)
   * @param {string} vesselId - Vessel ID
   * @param {string} userId - User ID
   * @param {string} accessLevel - Access level (e.g., 'owner', 'manager', 'viewer')
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  adminGrantVesselAccess: async (vesselId, userId, accessLevel) => {
    try {
      const response = await apiRequest(`/admin/vessels/${vesselId}/grant-access`, {
        method: 'POST',
        body: JSON.stringify({ userId, accessLevel })
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to grant vessel access' };
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Revoke vessel access from user (super_admin only)
   * @param {string} vesselId - Vessel ID
   * @param {string} userId - User ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  adminRevokeVesselAccess: async (vesselId, userId) => {
    try {
      const response = await apiRequest(`/admin/vessels/${vesselId}/revoke-access/${userId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to revoke vessel access' };
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Get all organizations (super_admin only)
   * @param {number} page - Page number (default 1)
   * @param {number} limit - Items per page (default 50)
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  adminGetOrganizations: async (page = 1, limit = 50) => {
    try {
      const response = await apiRequest(`/admin/organizations?page=${page}&limit=${limit}`);

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to fetch organizations' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.organizations, pagination: data.pagination };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Create new organization (super_admin only)
   * @param {Object} orgData - Organization data
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  adminCreateOrganization: async (orgData) => {
    try {
      const response = await apiRequest('/admin/organizations/create', {
        method: 'POST',
        body: JSON.stringify(orgData)
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to create organization' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.organization, tempPassword: data.tempPassword, owner: data.owner };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * FT-395 (Cluster C): Update an organization (super_admin only)
   * Server: PATCH /admin/organizations/:id — router-level requireSuperAdmin.
   * Body allowlist (updateOrganizationSchema): name, active.
   * Replaces the previous (hazardous) reuse of adminUpdateVessel with an ORG id.
   * @param {string} orgId - Organization ID
   * @param {Object} updates - Fields to update (name, active)
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  adminUpdateOrganization: async (orgId, updates) => {
    try {
      const response = await apiRequest(`/admin/organizations/${orgId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.message || error.error || 'Failed to update organization' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.organization };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * FT-395 (Cluster C): Delete an organization (super_admin only)
   * Server: DELETE /admin/organizations/:id — router-level requireSuperAdmin;
   * returns { success, organization }. Replaces the previous (hazardous) reuse of
   * adminDeleteVessel with an ORG id, which could have acted on a real VESSEL.
   * @param {string} orgId - Organization ID
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  adminDeleteOrganization: async (orgId) => {
    try {
      const response = await apiRequest(`/admin/organizations/${orgId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.message || error.error || 'Failed to delete organization' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.organization };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Get organization members (super_admin only)
   * @param {string} orgId - Organization ID
   * @param {number} page - Page number (default: 1)
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  adminGetOrgMembers: async (orgId, page = 1) => {
    try {
      const response = await apiRequest(`/admin/organizations/${orgId}/members?page=${page}`);

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to fetch organization members' };
      }

      const data = await safeParseResponse(response);
      return { success: true, data: data.members || [] };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Add member to organization (super_admin only)
   * @param {string} orgId - Organization ID
   * @param {string} userId - User ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  adminAddOrgMember: async (orgId, userId) => {
    try {
      const response = await apiRequest(`/admin/organizations/${orgId}/members/add`, {
        method: 'POST',
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to add organization member' };
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  /**
   * Admin: Remove member from organization (super_admin only)
   * @param {string} orgId - Organization ID
   * @param {string} userId - User ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  adminRemoveOrgMember: async (orgId, userId) => {
    try {
      const response = await apiRequest(`/admin/organizations/${orgId}/members/${userId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await safeParseResponse(response);
        return { success: false, error: error.error || 'Failed to remove organization member' };
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }
};

/**
 * Check if Azure API is configured
 *
 * ENTERPRISE PATTERN: Explicit environment variable check
 * This ensures we can distinguish between:
 * - Azure configured (REACT_APP_AZURE_API_URL set)
 * - Mock/offline mode (no env var set)
 *
 * @returns {boolean} True if Azure API is explicitly configured via environment variable
 */
export const isAzureApiConfigured = () => {
  // Explicitly check if REACT_APP_AZURE_API_URL was set
  // This allows distinguishing between "use Azure" and "use mock/offline"
  const envUrl = process.env.REACT_APP_AZURE_API_URL;

  if (!envUrl) {
    // No environment variable set - use mock/offline mode
    return false;
  }

  // Validate URL format
  try {
    new URL(envUrl);
    return true;
  } catch {
    console.error('[AZURE] Invalid REACT_APP_AZURE_API_URL format:', envUrl);
    return false;
  }
};

/**
 * Check if Azure API is available (health check)
 * @returns {Promise<boolean>}
 */
export const isAzureApiAvailable = async () => {
  try {
    const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) return false;

    const data = await safeParseResponse(response);
    return data.status === 'healthy';
  } catch {
    return false;
  }
};

export default azureApiClient;
